import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Instagram, Linkedin, Loader2, Sparkles } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";

const META = { meta: { label: "Instagram & Facebook", Icon: Instagram }, linkedin: { label: "LinkedIn", Icon: Linkedin } };
const ERR = { state: "The sign-in session expired. Please try again.", no_pages: "No Facebook Page was found on that account. Sign in with the account that manages your Page.", token_exchange: "We couldn't finish the connection. Please try again.", missing_code: "Access was not approved.", access_denied: "Access was not approved. Click Connect again and choose Allow.", user_cancelled_login: "Sign-in was cancelled. Click Connect to try again.", user_cancelled_authorize: "Access was not approved. Click Connect again and choose Allow." };

export default function ConnectInvite() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get(`/public/connect/${token}`).then(r => setInfo(r.data)).catch(e => setError(formatApiError(e.response?.data?.detail) || "This link is not valid."));
  }, [token]);

  const start = async (p) => {
    setBusy(p);
    try { const { data } = await api.get(`/public/connect/${token}/${p}/start`); window.location.href = data.authorize_url; }
    catch (e) { setError(formatApiError(e.response?.data?.detail)); setBusy(""); }
  };
  const flash = ["meta", "linkedin"].map(p => params.get(`${p}_error`) ? { p, ok: false, msg: ERR[params.get(`${p}_error`)] || "Something went wrong. Please try again." } : params.get(p) === "connected" ? { p, ok: true } : null).find(Boolean);

  return (
    <div className="min-h-screen bg-[#F5F7FB] grid place-items-center p-4">
      <div className="panel p-8 w-full max-w-md space-y-5" data-testid="connect-invite-page">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 grid place-items-center"><Sparkles className="w-4 h-4 text-white" /></div>
          <div className="text-sm font-bold">Shloka<span className="text-orange-500">SMM</span></div>
        </div>
        {error && <div className="text-sm text-rose-700" data-testid="connect-invite-error">{error}</div>}
        {!info && !error && <div className="flex items-center gap-2 text-sm text-neutral-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
        {info && (
          <>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight">Connect {info.brand_name}</h1>
              <p className="text-sm text-neutral-600 mt-1">Sign in with the account that manages your page and approve access. We never see your password.</p>
            </div>
            {flash && <div className={`text-sm rounded-md px-3 py-2 border ${flash.ok ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-rose-50 border-rose-300 text-rose-700"}`} data-testid="connect-invite-flash">{flash.ok ? `${META[flash.p].label} connected. Thank you!` : flash.msg}</div>}
            <div className="space-y-3">
              {info.providers.map(p => {
                const { label, Icon } = META[p];
                const done = info.connected.includes(p);
                return (
                  <div key={p} className="flex items-center gap-3 border border-[#E4E7EC] rounded-lg p-3" data-testid={`connect-invite-${p}`}>
                    <Icon className="w-5 h-5 text-neutral-700" />
                    <span className="flex-1 font-medium">{label}</span>
                    {done && <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Connected</span>}
                    {!info.configured[p] && <span className="text-[11px] text-neutral-500">Not available yet</span>}
                    <Button size="sm" disabled={!!busy || !info.configured[p]} onClick={() => start(p)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid={`connect-invite-${p}-btn`}>
                      {busy === p ? "Opening…" : done ? "Reconnect" : "Connect"}
                    </Button>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-neutral-500">You can close this page once everything shows Connected.</p>
          </>
        )}
      </div>
    </div>
  );
}
